import React from "react";
import BreadcrumbStrip from "./BreadcrumbStrip";

/**
 * PageLayout - Standardized layout wrapper for all pages
 *
 * @param {Object} props
 * @param {React.ReactNode} props.children - Page content
 * @param {string} props.title - Page title for header
 * @param {string} props.description - Page description
 * @param {Array} props.breadcrumbs - Breadcrumb items [{ label, path }]
 * @param {boolean} props.showBreadcrumbs - Show breadcrumb strip (default: true)
 * @param {string} props.maxWidth - Content max width ('sm' | 'md' | 'lg' | 'xl' | 'full')
 * @param {React.ReactNode} props.actions - Optional action buttons for the header
 */
const PageLayout = ({
  children,
  title,
  description,
  breadcrumbs = [],
  showBreadcrumbs = true,
  maxWidth = "lg",
  actions,
  className = "",
}) => {
  const getMaxWidthClass = () => {
    const widths = {
      sm: "max-w-3xl",
      md: "max-w-5xl",
      lg: "max-w-7xl",
      xl: "max-w-[1400px]",
      full: "max-w-none",
    };
    return widths[maxWidth] || widths.lg;
  };

  return (
    <>
      {/* Breadcrumbs */}
      {showBreadcrumbs && breadcrumbs.length > 0 && (
        <BreadcrumbStrip items={breadcrumbs} />
      )}

      {/* Main Content */}
      <div className={`flex-1 ${className}`}>
        <div
          className={`${getMaxWidthClass()} mx-auto px-4 sm:px-6 lg:px-8 py-6 md:py-8`}
        >
          {/* Page Header */}
          {(title || description || actions) && (
            <header className="page-header mb-8">
              <div className="flex items-start justify-between gap-4">
                <div>
                  {title && <h1 className="page-title">{title}</h1>}
                  {description && <p className="page-description">{description}</p>}
                </div>
                {actions && <div className="flex-shrink-0">{actions}</div>}
              </div>
            </header>
          )}

          {/* Page Content */}
          {children}
        </div>
      </div>
    </>
  );
};

/**
 * PageSection - Consistent section wrapper within pages
 */
export const PageSection = ({ children, title, description, action, className = "" }) => {
  return (
    <section className={`page-section ${className}`}>
      {(title || action) && (
        <div className="section-header">
          <div>
            {title && <h2 className="section-title">{title}</h2>}
            {description && <p className="text-sm text-muted-foreground mt-1">{description}</p>}
          </div>
          {action && <div className="section-action">{action}</div>}
        </div>
      )}
      {children}
    </section>
  );
};

/**
 * PageCard - Consistent card wrapper for content blocks
 */
export const PageCard = ({
  children,
  title,
  description,
  footer,
  className = "",
  padding = true,
}) => {
  return (
    <div className={`card ${className}`}>
      {(title || description) && (
        <div className="card-header">
          {title && <h3 className="card-title">{title}</h3>}
          {description && <p className="card-description">{description}</p>}
        </div>
      )}
      <div className={padding ? "card-content" : ""}>{children}</div>
      {footer && <div className="card-footer">{footer}</div>}
    </div>
  );
};

export default PageLayout;
